/**
 * High-Performance Shopify Customer Wishlist Manager
 * Optimized for core web vitals, non-blocking execution, and low TBT (Total Blocking Time).
 */
class CustomerWishlist {
  constructor() {
    this.customerId = window.ShopifyCustomer?.id || null;
    this.storageKey = 'shopify_wishlist';
    this.itemsCache = null; // Memory cache to avoid repeated LocalStorage reads
    this.eventsBound = false;
    
    // Defer initialization to avoid blocking paint/render phases
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.init(), { timeout: 2000 });
    } else {
      setTimeout(() => this.init(), 1);
    }
  }

  init() {
    this.items = this.getWishlist();
    
    if (this.customerId && !localStorage.getItem(`shopify_wishlist_synced_${this.customerId}`)) {
      this.mergeGuestItemsToAccount();
    }

    // Schedule UI DOM mutations in a single batch animation frame
    requestAnimationFrame(() => {
      this.updateBadges();
      this.syncButtonStates();
    });

    this.bindEvents();
  }

  /**
   * Fast in-memory cached retrieval of local storage
   */
  getWishlist() {
    if (this.itemsCache !== null) return this.itemsCache;
    
    try {
      const localData = localStorage.getItem(this.storageKey);
      this.itemsCache = localData ? JSON.parse(localData) : [];
    } catch (e) {
      this.itemsCache = [];
    }
    return this.itemsCache;
  }

  saveWishlist(items) {
    // Unique filtering in-place
    this.itemsCache = Array.from(new Set(items));
    
    // Write to LocalStorage (Deferred so UI thread isn't blocked)
    setTimeout(() => {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.itemsCache));
      } catch (e) {
        console.error('Failed to save wishlist:', e);
      }
    }, 0);

    // Batch UI updates using requestAnimationFrame
    requestAnimationFrame(() => {
      this.updateBadges();
      this.syncButtonStates();
    });

    if (this.customerId) {
      this.syncToCustomerAccount(this.itemsCache);
    }
  }

  add(productHandle) {
    if (!productHandle) return;
    const items = this.getWishlist();
    if (!items.includes(productHandle)) {
      items.push(productHandle);
      this.saveWishlist(items);
    }
  }

  remove(productHandle) {
    if (!productHandle) return;
    const items = this.getWishlist();
    const index = items.indexOf(productHandle);
    if (index > -1) {
      items.splice(index, 1);
      this.saveWishlist(items);
    }
  }

  toggleItem(productHandle) {
    if (!productHandle) return;
    const items = this.getWishlist();
    if (items.includes(productHandle)) {
      this.remove(productHandle);
    } else {
      this.add(productHandle);
    }
  }

  /**
   * Single-pass DOM update batching to avoid Layout Thrashing
   */
  syncButtonStates() {
    const itemsSet = new Set(this.itemsCache || []);
    const buttons = document.querySelectorAll('[data-wishlist-handle], .js-wishlist-btn, [data-product-handle]');
    
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      const handle = button.getAttribute('data-wishlist-handle') 
        || button.dataset.productHandle 
        || button.getAttribute('data-product-handle');

      const isActive = itemsSet.has(handle);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }

  updateBadges() {
    const count = (this.itemsCache || []).length;
    const badges = document.querySelectorAll('#WishlistCountBadge, .js-wishlist-count, #wishlist-count');
    
    for (let i = 0; i < badges.length; i++) {
      const badge = badges[i];
      badge.textContent = count;
      badge.style.display = count > 0 ? 'inline-flex' : 'none';
      badge.classList.add('is-updated');
    }
    
    // Remove class after animation finishes
    setTimeout(() => {
      for (let i = 0; i < badges.length; i++) {
        badges[i].classList.remove('is-updated');
      }
    }, 300);
  }

  async syncToCustomerAccount(items) {
    try {
      await fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form_type': 'customer',
          'utf8': '✓',
          'customer[metafields][custom][wishlist]': JSON.stringify(items)
        })
      });
    } catch (err) {
      console.error('Failed to sync customer wishlist:', err);
    }
  }

  async mergeGuestItemsToAccount() {
    const guestItems = this.getWishlist();
    if (guestItems.length > 0) {
      await this.syncToCustomerAccount(guestItems);
    }
    localStorage.setItem(`shopify_wishlist_synced_${this.customerId}`, 'true');
  }

  bindEvents() {
    if (this.eventsBound) return;

    // Use event delegation on body
    document.body.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-handle], .js-wishlist-btn, [data-product-handle]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        
        const handle = btn.getAttribute('data-wishlist-handle') 
          || btn.dataset.productHandle 
          || btn.getAttribute('data-product-handle');
          
        this.toggleItem(handle);
      }
    }, { passive: false });

    this.eventsBound = true;
  }
}

// Global initialization & API bindings
function initWishlistManager() {
  if (!window.wishlistManager) {
    const manager = new CustomerWishlist();
    window.wishlistManager = {
      instance: manager,
      getWishlist: () => manager.getWishlist(),
      add: (handle) => manager.add(handle),
      remove: (handle) => manager.remove(handle),
      toggle: (handle) => manager.toggleItem(handle)
    };
  }
}

// Execute initialization non-blockingly
if (window.addEventListener) {
  window.addEventListener('load', initWishlistManager, { once: true });
} else {
  initWishlistManager();
}

// Shopify Theme Editor reload support
document.addEventListener('shopify:section:load', () => {
  if (window.wishlistManager?.instance) {
    window.wishlistManager.instance.init();
  }
});